import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { useObjectStore } from "../zustand/objects";
import { LoadingOverlay, Card, Button, Text, Select, Modal, SimpleGrid, Box, Stack } from '@mantine/core';
import {
  editorModalShell,
  editorModalStrongTitleStyles,
  editorModalGalleryPanelStyleTall,
  editorModalAccent,
  editorModalFootnoteBoxStyle,
  getStoryboardChromeTheme,
  MBX_DARK_BACKGROUND_FILL,
  projectStyle,
  storyboardCardMutedContentSectionStyle,
  storyboardCardRootStyleFromTheme,
  storyboardCardThumbStyleFromTheme,
  storyboardSceneButtonStyles,
} from "../styleFile";

import { ScreenshotForProjectsPage } from "./Screenshot"

import {getTemplateScenes} from "../api.js"
import { getViewportSceneTheme } from "../utils/viewportCanvasDefaults";

/** In-memory cache: reopening the same category skips the network round-trip. */
const templateScenesCache = new Map();

/**
 * Each template card used to mount ScreenshotForProjectsPage immediately — that is a full R3F/WebGL
 * canvas per card, so a grid of N templates meant N simultaneous GL contexts (very slow).
 * We only mount the preview after the card scrolls near the viewport.
 */
function LazyTemplatePreview({ objectsAreLoaded, artboard, previewKey, thumbBg, floorColour }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(false);
  }, [previewKey]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) setVisible(true);
      },
      { root: null, rootMargin: "160px 0px", threshold: 0 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [previewKey]);

  return (
    <div
      ref={ref}
      style={{
        ...projectStyle.container.projectScreenshotContainer,
        position: "absolute",
        inset: 0,
        minHeight: 0,
        backgroundColor: thumbBg,
      }}
    >
      {visible && objectsAreLoaded ? (
        <ScreenshotForProjectsPage artboard={artboard} floorColour={floorColour} />
      ) : null}
    </div>
  );
}

export function TemplateModal({ open, close, category, projectKey }) {
  
  const {
    artboards,
    addArtboardTemplatePositional,
    arrangeUndoRedo,
    currentObjectArtboard,
    objectsAreLoaded,
    viewportThemeOverride,
    floorColour,
  } = useObjectStore();
  const isDarkChrome = viewportThemeOverride === "dark";
  const sbTheme = getStoryboardChromeTheme(isDarkChrome);
  /** Same clear/letterbox color as `PreviewViewport` behind the WebGL canvas. */
  const templatePreviewChromeBg = useMemo(
    () =>
      viewportThemeOverride === "dark"
        ? MBX_DARK_BACKGROUND_FILL
        : getViewportSceneTheme("light").background,
    [viewportThemeOverride]
  );

  const [loading, setLoading] = useState(false);
  const [scenes, setScenes] = useState();

  /** Show overlay synchronously before paint when we know a fetch is required */
  useLayoutEffect(() => {
    if (!open || !category) return;
    if (!templateScenesCache.has(category)) setLoading(true);
  }, [open, category]);

  useEffect(() => {
    if (!open) return;
    if (!category) {
      setLoading(false);
      return;
    }
    const cached = templateScenesCache.get(category);
    if (cached) {
      setScenes(cached);
      setLoading(false);
      return;
    }
    let cancelled = false;
    getTemplateScenes(category)
      .then((e) => {
        if (cancelled) return;
        if (e && Array.isArray(e)) {
          templateScenesCache.set(category, e);
          setScenes(e);
        } else {
          setScenes([]);
        }
      })
      .catch(() => {
        if (!cancelled) setScenes([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, category]);

const handleSubmit = (index, val) => {
   // send current state to undoActions
   arrangeUndoRedo({
    updateType: "artboardAdd",
    undo: false,
    projectKey: projectKey,
    artboard: currentObjectArtboard
  })
  // send update to zustand
    addArtboardTemplatePositional(index - 1, val)
}

// console.log(scenes && scenes[0])
    

  return (
    <Modal
      style={{ zIndex: 10 }}
      opened={open}
      onClose={close}
      title={category ? `Template · ${category}` : "Templates"}
      withCloseButton
      size={720}
      radius={editorModalShell.radius}
      centered={editorModalShell.centered}
      overlayProps={editorModalShell.overlayProps}
      styles={editorModalStrongTitleStyles}
    >
      <Box pos="relative" style={{ minHeight: 480 }}>
        <LoadingOverlay
          loaderProps={{ color: isDarkChrome ? "white" : "black" }}
          visible={loading}
          zIndex={1000}
          overlayProps={{ radius: "sm", blur: 2 }}
        />
        <Box style={editorModalGalleryPanelStyleTall}>
          <Stack gap="md" mb="md">
            {category && (
              <Text size="sm" style={{ lineHeight: 1.5, color: sbTheme.mutedTextColor }}>
                Add a {category} template scene to your project. Pick which artboard it should populate.
              </Text>
            )}
          </Stack>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md" verticalSpacing="md">
            {scenes &&
              !loading &&
              scenes.map((mapped, i) => (
                <Card
                  key={i}
                  p={0}
                  radius={editorModalShell.radius}
                  withBorder={false}
                  shadow="sm"
                  styles={{
                    root: {
                      ...storyboardCardRootStyleFromTheme(sbTheme),
                      "&:hover": {
                        boxShadow: sbTheme.hoverShadow,
                      },
                    },
                  }}
                >
                  <Card.Section p={0}>
                    <Box style={{ position: "relative", ...storyboardCardThumbStyleFromTheme(sbTheme) }}>
                      <LazyTemplatePreview
                        previewKey={`${category}-${mapped.Name}-${i}`}
                        objectsAreLoaded={objectsAreLoaded}
                        artboard={mapped.scene}
                        thumbBg={templatePreviewChromeBg}
                        floorColour={floorColour}
                      />
                    </Box>
                  </Card.Section>
                  <Box style={storyboardCardMutedContentSectionStyle(sbTheme)}>
                    <Stack gap="sm" px="md" pt="md" pb="md">
                      <Text
                        fw={600}
                        fz={13}
                        c={sbTheme.titleColor}
                        style={{
                          lineHeight: 1.3,
                        }}
                      >
                        {mapped.Name}
                      </Text>
                      <Text size="xs" style={{ lineHeight: 1.45, color: sbTheme.bodyTextColor }}>
                        {mapped.storyboard_fields.description}
                      </Text>
                      <form
                        style={{ width: "100%" }}
                        onSubmit={(e) => {
                          e.preventDefault();
                          handleSubmit(e.target[1].value, mapped);
                        }}
                      >
                        <Select
                          action="clear"
                          label="Scene"
                          placeholder="1"
                          data={[...Object.keys(artboards)]}
                          defaultValue={1}
                          radius="md"
                          styles={{
                            label: { color: sbTheme.titleColor },
                            input: {
                              borderColor: sbTheme.cardBorder,
                              backgroundColor: sbTheme.cardBg,
                              color: sbTheme.titleColor,
                              "&:focus": { borderColor: editorModalAccent },
                            },
                          }}
                        />
                        <Button type="submit" mt="md" fullWidth styles={storyboardSceneButtonStyles}>
                          Add template
                        </Button>
                      </form>
                    </Stack>
                  </Box>
                </Card>
              ))}
          </SimpleGrid>
          <Box
            mt="lg"
            p="md"
            style={{
              ...editorModalFootnoteBoxStyle,
              backgroundColor: sbTheme.footnoteBg,
              borderColor: sbTheme.cardBorder,
            }}
          >
            <Text size="xs" style={{ lineHeight: 1.55, color: sbTheme.mutedTextColor }}>
              Templates copy a pre-built layout into the scene you select. You can undo after adding if needed.
            </Text>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}

